/**
 * useTimeClockQueries.ts
 * 
 * Standalone React Query hooks for Time & Attendance data (team members, crews, time logs).
 * 
 * TimeClockContext.tsx internally delegates to these hooks for backward compatibility.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useTallerListo } from './useTallerListo';
import { TeamMember, Crew, TimeLog } from '../types';
import {
    fetchTeamMembers, addTeamMemberDoc, updateTeamMemberDoc, removeTeamMemberDoc,
    fetchCrews, addCrewDoc, updateCrewDoc, deleteCrewDoc,
    addTimeLogDoc, updateTimeLogDoc, deleteTimeLogDoc, fetchTimeLogs,
    transferShiftDoc
} from '../services/database';
import type { TransferShiftArgs } from '../services/database';

export const TIME_CLOCK_KEYS = {
    teamMembers: (wsId: string) => ['teamMembers', wsId] as const,
    crews:       (wsId: string) => ['crews', wsId] as const,
    allLogs:     (wsId: string) => ['time-logs', wsId, 'all'] as const,
};

// La puerta de arranque (`useTallerListo`) vive en hooks/useTallerListo.ts,
// compartida con proyectos, flota, servicios y estimados.

/**
 * Every time log in the workspace.
 *
 * Needed by job costing, which has to attribute man-hours across ALL projects at
 * once — the per-project hook can't answer "what did every job cost".
 */
export function useAllTimeLogs() {
    const { wsId, listo } = useTallerListo();

    return useQuery<TimeLog[]>({
        queryKey: TIME_CLOCK_KEYS.allLogs(wsId),
        // Flecha, no `queryFn: fetchTimeLogs`. React Query invoca la función
        // de consulta con su propio QueryFunctionContext como PRIMER argumento,
        // así que pasarla por referencia le colaría ese objeto justo donde va
        // el id del taller. Vale para todas las consultas de este archivo.
        queryFn: () => fetchTimeLogs(wsId),
        enabled: listo,
        staleTime: 60_000,
    });
}

export function useTeamMembers() {
    const { wsId, listo } = useTallerListo();

    // Read is open to any authenticated user in the workspace so workers can
    // see the names of their own crew's members. Management UI (add / edit /
    // delete team members) is still gated to Admin/Office in the components.
    return useQuery({
        queryKey: TIME_CLOCK_KEYS.teamMembers(wsId),
        queryFn: () => fetchTeamMembers(wsId),
        enabled: listo,
    });
}

export function useCrews() {
    const { wsId, listo } = useTallerListo();

    // Read is open to any authenticated user in the workspace so workers see
    // their own crew label on their visits. The management UI (Crews tab,
    // create/edit/delete) is still gated to Admin/Office at the component
    // level, and crew mutations have their own RLS on the server.
    return useQuery({
        queryKey: TIME_CLOCK_KEYS.crews(wsId),
        queryFn: () => fetchCrews(wsId),
        enabled: listo,
    });
}

export function useAddTeamMember() {
    const qc = useQueryClient();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async (member: TeamMember) => {
            await addTeamMemberDoc(member, wsId);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['teamMembers'] }),
    });
}

export function useUpdateTeamMember() {
    const qc = useQueryClient();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<TeamMember> }) => {
            await updateTeamMemberDoc(id, updates, wsId);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['teamMembers'] }),
    });
}

export function useRemoveTeamMember() {
    const qc = useQueryClient();
    const { activeWorkspace } = useWorkspace();
    const wsId = activeWorkspace?.id || 'default';

    return useMutation({
        mutationFn: async (id: string) => {
            await removeTeamMemberDoc(id, wsId);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['teamMembers'] }),
    });
}

export function useAddCrew() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: async (crew: Omit<Crew, 'id'>) => {
            await addCrewDoc(crew);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['crews'] }),
    });
}

export function useUpdateCrew() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<Crew> }) => {
            await updateCrewDoc(id, updates);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['crews'] }),
    });
}

export function useDeleteCrew() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            await deleteCrewDoc(id);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['crews'] }),
    });
}

// Active time logs are synced via the realtime channel in TimeClockContext.
// Mutations also invalidate the per-project cache (`['time-logs']`) so the
// ProjectDetail Vitals bar + Activity feed refresh on clock-in/out.
export function useAddTimeLog() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (data: Omit<TimeLog, 'id' | 'clockOut'>) => {
            await addTimeLogDoc(data);
        },
        // PersonalClockPanel shows its own friendly toasts (incl. the
        // ALREADY_OPEN_SHIFT case) — suppress the global raw-message backstop
        // so users don't get two toasts for one failure.
        meta: { suppressGlobalError: true },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['time-logs'] }),
        onError: (err: any) => {
            // If the server rejected because a shift is already open, refresh
            // the cache so the existing open shift becomes visible.
            if (err?.message === 'ALREADY_OPEN_SHIFT') {
                qc.invalidateQueries({ queryKey: ['time-logs'] });
            }
        },
    });
}

/**
 * Pasar el reloj de un destino a otro. Cierra un tramo y abre otro, así que un
 * traspaso dispara DOS eventos de realtime (el UPDATE del que se cierra y el
 * INSERT del que se abre) y cada cliente conectado vuelve a consultar dos
 * veces. Con el volumen de este reloj sale más barato que cualquier intento de
 * juntarlos: agrupar obligaría a retrasar la primera consulta, y entonces la
 * pantalla de quien acaba de tocar el botón tarda en enseñar el destino nuevo.
 */
export function useTransferShift() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (args: TransferShiftArgs) => transferShiftDoc(args),
        // Cada marcador de `TRANSFER_MARKERS` tiene su frase en
        // `reloj.transferir.*`: quien llama enseña ESA, y el respaldo global
        // (que pinta el mensaje crudo del servidor) sobraría y saldría además.
        meta: { suppressGlobalError: true },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['time-logs'] }),
    });
}

export function useUpdateTimeLog() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<TimeLog> }) => {
            await updateTimeLogDoc(id, updates);
        },
        // Cada marcador de `updateTimeLogDoc` (INVALID_TIME_LOG,
        // ALREADY_OPEN_SHIFT, TIME_LOG_NOT_UPDATED) tiene su frase en
        // TimeLogEditorModal, que la enseña DENTRO del diálogo. Sin esto el
        // respaldo global salía además, y encima con el marcador CRUDO: el
        // capataz veía «INVALID_TIME_LOG» en un aviso flotante. Igual que en
        // `useAddTimeLog` y `useTransferShift`.
        //
        // Ningún camino se queda mudo: por esta mutación pasan además
        // `clockOut`, `startLunch` y `endLunch` de TimeClockContext, y los tres
        // llamadores (`PersonalClockPanel`) ya atrapan el fallo y sacan su
        // propio aviso. Lo único que se quita es el segundo aviso.
        meta: { suppressGlobalError: true },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['time-logs'] }),
    });
}

export function useDeleteTimeLog() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await deleteTimeLogDoc(id);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['time-logs'] }),
    });
}
